import React, { useEffect, useState } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '../ui/sheet';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Loader2 } from 'lucide-react';
import api from '../../utils/axios';
import { EstadoBadge } from './EstadoBadge';
import { EventoItem } from './EventoItem';
import { fmtDuracion, fmtFechaHora, haceCuanto } from './actividadUtils';

const Vacio = ({ txt }) => <p className="py-10 text-center text-sm text-slate-400">{txt}</p>;

const Secciones = ({ items }) => (items.length ? (
  <table className="w-full text-sm" data-testid="act-detalle-secciones">
    <thead className="text-[11px] uppercase tracking-wider text-slate-500"><tr>
      <th className="py-2 text-left">Sección</th><th className="py-2 text-right">Visitas</th><th className="py-2 text-right">Tiempo</th><th className="py-2 text-right">Última vez</th>
    </tr></thead>
    <tbody className="divide-y divide-slate-100">
      {items.map((s) => (
        <tr key={s.seccion}>
          <td className="py-2"><p className="font-semibold text-slate-700">{s.seccion}</p>{s.modulo && <p className="text-xs text-slate-400">{s.modulo}</p>}</td>
          <td className="py-2 text-right tabular-nums">{s.vistas}</td>
          <td className="py-2 text-right tabular-nums">{fmtDuracion(s.tiempo_seg)}</td>
          <td className="py-2 text-right text-xs text-slate-500">{haceCuanto(s.ultima)}</td>
        </tr>
      ))}
    </tbody>
  </table>
) : <Vacio txt="Sin pantallas registradas en el período." />);

const Dispositivos = ({ items }) => (items.length ? (
  <ul className="divide-y divide-slate-100" data-testid="act-detalle-dispositivos">
    {items.map((d, i) => (
      <li key={i} className="py-2.5 flex items-center justify-between gap-3 text-sm">
        <div><p className="font-semibold text-slate-700">{[d.dispositivo, d.navegador, d.so].filter(Boolean).join(' · ')}</p>
          <p className="font-mono text-xs text-slate-400">{d.ip || 'IP desconocida'}</p></div>
        <div className="text-right text-xs text-slate-500"><p>{d.veces} ingreso{d.veces > 1 ? 's' : ''}</p><p>{fmtFechaHora(d.ultimo)}</p></div>
      </li>
    ))}
  </ul>
) : <Vacio txt="Sin ingresos registrados en el período." />);

const Lista = ({ items, vacio, testid }) => (items.length ? (
  <ul className="divide-y divide-slate-100" data-testid={testid}>
    {items.map((e, i) => <EventoItem key={e.id || `${e.fecha}-${i}`} e={e} mostrarUsuario={false} testid={`${testid}-${i}`} />)}
  </ul>
) : <Vacio txt={vacio} />);

export const UsuarioActividadSheet = ({ usuario, dias, onOpenChange }) => {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!usuario) return;
    setData(null);
    api.get(`/actividad-usuarios/usuario/${usuario.id}`, { params: { dias } }).then((r) => setData(r.data)).catch(() => setData({ error: true }));
  }, [usuario, dias]);

  return (
    <Sheet open={!!usuario} onOpenChange={onOpenChange}>
      <SheetContent className="vig-scope w-full sm:max-w-2xl overflow-y-auto" data-testid="act-detalle-sheet">
        <SheetHeader>
          <SheetTitle className="text-2xl font-extrabold" style={{ fontFamily: 'Manrope, sans-serif' }}>{usuario?.nombre || usuario?.email}</SheetTitle>
          <SheetDescription className="flex flex-wrap items-center gap-2">
            {usuario?.email} · {usuario?.rol} {usuario && <EstadoBadge estado={usuario.estado} />}
          </SheetDescription>
        </SheetHeader>
        {usuario && (
          <div className="grid grid-cols-3 gap-3 my-5">
            {[
              ['Ingresos', data?.logins ? data.logins.filter((e) => e.tipo === 'LOGIN').length : usuario.logins],
              ['Tiempo de uso', fmtDuracion(data?.secciones ? data.secciones.reduce((s, x) => s + x.tiempo_seg, 0) : usuario.tiempo_seg)],
              ['Acciones', data?.acciones ? data.acciones.length : usuario.acciones],
            ].map(([l, v]) => (
              <div key={l} className="rounded-xl border border-slate-200 p-3"><p className="text-[11px] uppercase tracking-wider text-slate-500">{l}</p>
                <p className="text-xl font-bold tabular-nums text-slate-900">{v}</p></div>
            ))}
          </div>
        )}
        {!data ? <div className="py-16 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-teal-700" /></div> : data.error ? <Vacio txt="No se pudo cargar el detalle." /> : (
          <Tabs defaultValue="ingresos">
            <TabsList className="w-full grid grid-cols-4">
              <TabsTrigger value="ingresos" data-testid="act-tab-ingresos">Ingresos</TabsTrigger>
              <TabsTrigger value="secciones" data-testid="act-tab-secciones">Secciones</TabsTrigger>
              <TabsTrigger value="acciones" data-testid="act-tab-acciones">Acciones</TabsTrigger>
              <TabsTrigger value="dispositivos" data-testid="act-tab-dispositivos">Desde dónde</TabsTrigger>
            </TabsList>
            <TabsContent value="ingresos"><Lista items={data.logins} vacio="Sin ingresos registrados en el período." testid="act-detalle-ingresos" /></TabsContent>
            <TabsContent value="secciones"><Secciones items={data.secciones} /></TabsContent>
            <TabsContent value="acciones"><Lista items={data.acciones} vacio="Sin acciones en el período." testid="act-detalle-acciones" /></TabsContent>
            <TabsContent value="dispositivos"><Dispositivos items={data.dispositivos} /></TabsContent>
          </Tabs>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default UsuarioActividadSheet;
